import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  TablePagination,
  Typography,
} from '@mui/material';
import { harnessService } from '@/services/api/harnessService';
import { formatDecoded } from '@/features/harness/utils/formatDecoded';

const PAGE_SIZE = 50;

/** Decode a base64 payload's byte length without materialising a Uint8Array we don't otherwise need. */
function base64ByteLength(base64: string): number {
  if (!base64) return 0;
  try {
    return atob(base64).length;
  } catch {
    return 0;
  }
}

export interface CommunicationLogViewProps {
  /** The Batch whose persisted Communication Log to page through. */
  batchId: string;
}

/**
 * Read-only, paged Communication Log for reviewing a past Batch (Story 3.1, AC2). Unlike
 * {@link import('./LiveCommunicationLog').LiveCommunicationLog} — which renders the live local
 * frame buffer during recording — this fetches persisted frames from the backend (ordered by
 * timestamp, with Measurement attribution) and never touches a device. Sent/received frames are
 * visually distinct, and each frame shows the Measurement it was attributed to (or "unattributed"
 * for watchdog/unsolicited frames).
 */
export function CommunicationLogView({ batchId }: CommunicationLogViewProps) {
  // MUI TablePagination is 0-based; the backend endpoint is 1-based.
  const [page, setPage] = useState(0);

  const { data, isLoading, error } = useQuery({
    queryKey: ['harness-communication-log', batchId, page],
    queryFn: () => harnessService.getCommunicationLog(batchId, page + 1, PAGE_SIZE),
  });

  const frames = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" gutterBottom>
        Communication Log ({totalCount})
      </Typography>

      {isLoading && <CircularProgress size={20} />}
      {error && (
        <Typography variant="body2" color="error">
          Failed to load the Communication Log.
        </Typography>
      )}

      {!isLoading && !error && totalCount === 0 && (
        <Typography variant="body2" color="text.secondary">
          No communication was recorded for this Batch.
        </Typography>
      )}

      {!isLoading && !error && totalCount > 0 && (
        <>
          <List dense disablePadding sx={{ maxHeight: 400, overflow: 'auto' }}>
            {frames.map((frame) => {
              const decoded = formatDecoded(frame.decodedJson);
              return (
                <ListItem key={frame.id} disableGutters sx={{ alignItems: 'flex-start' }}>
                  <Chip
                    size="small"
                    label={frame.direction}
                    color={frame.direction === 'Sent' ? 'primary' : 'secondary'}
                    sx={{ mr: 1.5, mt: 0.25, minWidth: 80 }}
                  />
                  {!frame.crcValid && (
                    <Chip
                      size="small"
                      label="CRC error"
                      color="error"
                      variant="outlined"
                      sx={{ mr: 1.5, mt: 0.25 }}
                    />
                  )}
                  <ListItemText
                    primary={`${base64ByteLength(frame.rawBytes)} bytes`}
                    secondary={
                      <>
                        {`${new Date(frame.timestamp).toLocaleString()} — ` +
                          (frame.measurementSequenceNumber != null
                            ? `Measurement #${frame.measurementSequenceNumber}`
                            : 'unattributed')}
                        {decoded && (
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{
                              display: 'block',
                              fontFamily: 'monospace',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            title={decoded}
                          >
                            {decoded}
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
              );
            })}
          </List>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
          />
        </>
      )}
    </Box>
  );
}
